import React, { useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'

import { Dialog, Loader } from '@percent/admin-dashboard/common/components'
import { DGR_STATUS, useGetDgrHelpers, useQueryList } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { CountrySpecificData, RegistryModalProps } from './RegistryModal.types'
import styles from './RegistryModal.module.scss'
import { sanitizeStringToRegex } from '@percent/utility'
import { Box, Button, Checkbox, FormField, Select, Spacer, TextInput, Typography } from '@percent/lemonade'
import { ActionsDialogAction } from '@percent/admin-dashboard/common/components/dialog/actionDialog/ActionDialog'
import { DgrStatus, PartnerName, RegistriesType, dgrStatuses } from '@percent/admin-dashboard/api/types'
import { OrganisationTypes } from '@percent/domain/verifications'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { filterMutuallyExclusiveOrganisationTypes } from '@percent/admin-dashboard/app/validations/nonprofitDetail/utils/filterMutuallyExclusiveOrganisationTypes'

export function RegistryModal({
  isOpened,
  setIsOpened,
  selectedRegistryData,
  nonprofitName,
  validationCountryCode,
  website,
  city,
  regexRegistryId,
  setSelectedRegistryData,
  organisationTypes,
  refreshRelatedOrganisations,
  refreshSimilarOrganisations,
  countrySpecificData,
  setCountrySpecificData,
  partnerName
}: RegistryModalProps) {
  const { t } = useTranslation()
  const { dgrOptions } = useGetDgrHelpers()
  const registriesSupportedTypesFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_REGISTRIES_SUPPORTED_TYPES)

  const { adminService } = useServices()
  const [success, setSuccess] = useState<boolean>(false)

  const isIndianValidationRequest = validationCountryCode === 'IND'
  const isAustralianValidationRequest = validationCountryCode === 'AUS'
  const queryParams = {
    countryCode: validationCountryCode,
    pageSize: undefined,
    supportedTypes:
      registriesSupportedTypesFeatureFlag && organisationTypes
        ? filterMutuallyExclusiveOrganisationTypes(organisationTypes, [])
        : undefined
  }

  const [{ dataOrNull, isLoading, errorMessageOrNull }, { query }] = useQueryList(
    adminService.getRegistries,
    queryParams
  )

  useEffect(() => {
    if (registriesSupportedTypesFeatureFlag) {
      query(queryParams)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registriesSupportedTypesFeatureFlag])

  const mapRegistries = (registries?: RegistriesType[] | null) => {
    return registries
      ? registries?.map(registry => {
          return {
            label: registry.englishName || registry.name,
            value: registry.id
          }
        })
      : []
  }

  const options = mapRegistries(dataOrNull)

  const isNonprofitOrganisation = organisationTypes?.includes(OrganisationTypes.Nonprofit)

  const isOnlyEducationOrganisation =
    organisationTypes?.length === 1 && organisationTypes.includes(OrganisationTypes.Education)

  const isIndianNonprofit = isIndianValidationRequest && isNonprofitOrganisation

  const dgrStatus = useMemo(() => {
    if (
      countrySpecificData?.dgrStatusList?.includes(dgrStatuses.AUS_DGR1) &&
      countrySpecificData?.dgrStatusList?.includes(dgrStatuses.AUS_DGR4)
    ) {
      return DGR_STATUS.AUS_DGR1_AND_4
    }

    if (countrySpecificData?.dgrStatusList?.includes(dgrStatuses.AUS_DGR1)) {
      return DGR_STATUS.AUS_DGR1
    }

    if (countrySpecificData?.dgrStatusList?.includes(dgrStatuses.AUS_DGR2)) {
      return DGR_STATUS.AUS_DGR2
    }

    return DGR_STATUS.NO_STATUS
  }, [countrySpecificData?.dgrStatusList])

  const { values, errors, handleBlur, setFieldValue, isValid, dirty, handleChange, touched, handleSubmit } = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues: {
      registryId: selectedRegistryData?.registry?.id,
      organisationRegistryId: selectedRegistryData?.organisationRegistryId,
      regex: selectedRegistryData?.registry?.regex,
      registryInformationNotFound: selectedRegistryData?.registryInformationNotFound || false,
      countrySpecificData: {
        ...(isIndianNonprofit ? { fcra: countrySpecificData?.fcra ?? '' } : {}),
        ...(isAustralianValidationRequest ? { dgrStatus } : {})
      }
    },
    validationSchema: () =>
      yup.lazy(value => {
        const newRegex = value.registryId
          ? dataOrNull?.find(registry => registry.id === value?.registryId)?.registryIdFormatRegex
          : regexRegistryId

        return yup.object().shape({
          registryInformationNotFound: yup.boolean(),
          registryId: yup
            .string()
            .when('registryInformationNotFound', { is: false, then: yup.string().required('Required') }),
          organisationRegistryId: yup.string().when('registryInformationNotFound', {
            is: false,
            then: yup
              .string()
              .matches(sanitizeStringToRegex(newRegex || ''), t('errorMessage.wrongFormatRegistryId'))
              .required('Required')
          }),
          countrySpecificData: yup.object().shape({
            fcra: yup
              .string()
              .matches(/^\d{8,10}$/, { message: t('errorMessage.wrongFormatFCRA'), excludeEmptyString: true })
              .optional()
          })
        })
      }),
    onSubmit: submitValues => {
      const { registryId, organisationRegistryId, registryInformationNotFound } = submitValues

      const registry = dataOrNull?.find(({ id }) => id === registryId)

      setSelectedRegistryData({
        registry: {
          id: registry?.id,
          name: registry?.name,
          englishName: registry?.englishName,
          regex: registry?.registryIdFormatRegex
        },
        organisationRegistryId,
        registryInformationNotFound
      })

      setCountrySpecificData?.(previousValue => {
        const mappedCountrySpecificData: CountrySpecificData = {}

        if (isIndianNonprofit && submitValues.countrySpecificData.fcra !== undefined) {
          mappedCountrySpecificData.fcra = submitValues.countrySpecificData.fcra
        }

        if (
          isAustralianValidationRequest &&
          submitValues.countrySpecificData.dgrStatus &&
          submitValues.countrySpecificData.dgrStatus !== DGR_STATUS.NO_STATUS
        ) {
          mappedCountrySpecificData.dgrStatusList = submitValues.countrySpecificData.dgrStatus
            .split(',')
            .map(status => status.trim() as DgrStatus)
        }

        return previousValue
          ? {
              ...previousValue,
              ...mappedCountrySpecificData
            }
          : mappedCountrySpecificData
      })
      setSuccess(true)

      if (registryInformationNotFound || isIndianNonprofit) {
        refreshSimilarOrganisations({
          registry: registry?.id,
          registryId: organisationRegistryId,
          countryCode: validationCountryCode,
          name: nonprofitName,
          city,
          website
        })
      }

      if (registry && organisationRegistryId && !registryInformationNotFound && !isIndianNonprofit) {
        refreshRelatedOrganisations({ registry: registry.id, registryId: organisationRegistryId })
      }
    }
  })

  const handleClose = () => {
    setIsOpened(false)
    setSuccess(false)
  }

  const isMicrosoftSocialImpact =
    partnerName?.toLowerCase() === PartnerName.MICROSOFT &&
    organisationTypes?.length === 1 &&
    organisationTypes.includes(OrganisationTypes.SocialImpact)

  const isGoodstackSocialImpact =
    [PartnerName.GOODSTACK, PartnerName.GOODSTACK_ADMIN, PartnerName.GOODSTACK_OUTSOURCED].some(
      partner => partner.toLowerCase() === partnerName?.toLowerCase()
    ) &&
    organisationTypes?.length === 1 &&
    organisationTypes.includes(OrganisationTypes.SocialImpact)

  return (
    <Dialog
      openModal={isOpened}
      onClose={handleClose}
      withoutHeader={success || !!errorMessageOrNull}
      headerTitle={t('dialog.reviewRegistry')}
    >
      {isLoading ? (
        <Loader />
      ) : errorMessageOrNull ? (
        <ActionsDialogAction
          handleOnClose={() => setIsOpened(false)}
          description={errorMessageOrNull}
          title={t('dialog.somethingWentWrong')}
          buttonTitle={t('button.done')}
          successIcon={false}
        />
      ) : success ? (
        <ActionsDialogAction
          handleOnClose={() => setIsOpened(false)}
          description={`${nonprofitName}${t('dialog.registryInfoUpdatedDescription')}`}
          title={t('dialog.registryInfoUpdated')}
          buttonTitle={t('button.done')}
          successIcon
        />
      ) : (
        <>
          <Box className={styles.modalBody}>
            <Typography variant="large" className={styles.bodyText}>
              {t('dialog.reviewRegistryDescription')} <span className={styles.organisationName}>{nonprofitName}</span>:
            </Typography>
          </Box>
          <form onSubmit={handleSubmit}>
            <Box className={styles.form}>
              <FormField
                label={t('dialog.approveValidationRequest.selectRegistry')}
                status={errors.registryId && touched.registryId ? 'danger' : 'default'}
                statusMessage={errors.registryId}
                data-testid="selectRegistry"
                disabled={!!values.registryInformationNotFound}
              >
                <Select
                  placeholder={t('dialog.approveValidationRequest.selectRegistry')}
                  searchable={false}
                  options={options}
                  disabled={!!values.registryInformationNotFound}
                  defaultValue={options.find(({ value }) => value === values?.registryId)}
                  onChange={event => {
                    setFieldValue('registryId', event.value)
                  }}
                />
              </FormField>
            </Box>
            <Box className={styles.form} data-testid="registry-id-input">
              <FormField
                label={t('dialog.approveValidationRequest.registryId')}
                status={errors.organisationRegistryId ? 'danger' : 'default'}
                statusMessage={errors.organisationRegistryId}
                data-testid="organisationRegistryId"
                disabled={!!values.registryInformationNotFound}
              >
                <TextInput
                  name="organisationRegistryId"
                  value={values.organisationRegistryId}
                  placeholder={t('dialog.approveValidationRequest.registryId')}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </FormField>

              {isIndianNonprofit && (
                <>
                  <Spacer size={4} axis="vertical" />
                  <FormField
                    label={t('dialog.approveValidationRequest.fcraId')}
                    necessity="optional"
                    description={t('dialog.approveValidationRequest.fcraIdDescription')}
                    status={errors.countrySpecificData?.fcra ? 'danger' : 'default'}
                    statusMessage={errors.countrySpecificData?.fcra}
                    data-testid="fcra"
                    disabled={!!values.registryInformationNotFound}
                  >
                    <TextInput
                      name="countrySpecificData.fcra"
                      value={values.countrySpecificData.fcra}
                      placeholder={t('dialog.approveValidationRequest.fcraId')}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </FormField>
                </>
              )}
              {isAustralianValidationRequest && (
                <>
                  <Spacer size={4} axis="vertical" />
                  <FormField
                    label={t('dialog.approveValidationRequest.dgrStatus')}
                    status={errors.countrySpecificData?.dgrStatus ? 'danger' : 'default'}
                    statusMessage={errors.countrySpecificData?.dgrStatus}
                    data-testid="dgrStatus"
                    disabled={!!values.registryInformationNotFound}
                  >
                    <Select
                      searchable={false}
                      options={dgrOptions}
                      placeholder={t('dialog.approveValidationRequest.dgrStatusPlaceholder')}
                      defaultValue={dgrOptions.find(option => option.value === values.countrySpecificData.dgrStatus)}
                      onChange={event => {
                        setFieldValue('countrySpecificData.dgrStatus', event.value)
                      }}
                    />
                  </FormField>
                </>
              )}
              {(isOnlyEducationOrganisation || isMicrosoftSocialImpact || isGoodstackSocialImpact) && (
                <>
                  <Spacer size={4} axis="vertical" />
                  <FormField
                    status={errors.registryInformationNotFound ? 'danger' : 'default'}
                    statusMessage={errors.registryInformationNotFound}
                    data-testid="registryInformationNotFound"
                  >
                    <Checkbox
                      name="registryInformationNotFound"
                      value={values.registryInformationNotFound?.toString()}
                      onBlur={handleBlur}
                      variant="default"
                      onChange={handleChange}
                      active={values.registryInformationNotFound}
                      label={t('dialog.approveValidationRequest.registryInformationNotFound')}
                    />
                  </FormField>
                </>
              )}
            </Box>

            <Box className={styles.modalBody}>
              <Typography variant="large" className={styles.bodyText}>
                {t('dialog.reviewRegistryDescriptionFooter')}
              </Typography>
            </Box>
            <Box className={styles.dialogFooter}>
              <Button
                disabled={!(dirty && isValid)}
                type="submit"
                variant="primary"
                data-testid="btn-update-information"
                loading={isLoading}
              >
                {t('button.updateInformation')}
              </Button>
              <Spacer size={4} />
              <Button
                variant="secondary"
                data-testid="btn-cancel-validiation"
                onClick={() => {
                  setIsOpened(false)
                }}
              >
                {t('button.cancel')}
              </Button>
            </Box>
          </form>
        </>
      )}
    </Dialog>
  )
}
